import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getAppraisal, type AppraisalDetail } from '../lib/appraisal';
import { categoryDotClass, listFieldPhotosForJob, type FieldPhotoDeviceInfo } from '../lib/fieldPhotos';
import { getItemsByJob, type QueueItem } from '../lib/syncQueue';

interface ReviewPhoto {
  id: string;
  sequence_index: number;
  device_info: FieldPhotoDeviceInfo;
  source: 'server' | 'local';
}

function mergeForReview(
  server: Awaited<ReturnType<typeof listFieldPhotosForJob>>,
  local: QueueItem[],
): ReviewPhoto[] {
  const map = new Map<string, ReviewPhoto>();
  for (const p of server) {
    map.set(p.id, {
      id: p.id,
      sequence_index: p.sequence_index,
      device_info: p.device_info,
      source: 'server',
    });
  }
  for (const q of local) {
    if (!map.has(q.photo_id)) {
      map.set(q.photo_id, {
        id: q.photo_id,
        sequence_index: q.sequence_index,
        device_info: (q.metadata.device_info ?? {}) as FieldPhotoDeviceInfo,
        source: 'local',
      });
    }
  }
  return Array.from(map.values()).sort(
    (a, b) => a.sequence_index - b.sequence_index,
  );
}

const ELEVATIONS = ['front', 'right', 'rear', 'left'] as const;

export function PreSyncReview() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [photos, setPhotos] = useState<ReviewPhoto[] | null>(null);
  const [job, setJob] = useState<AppraisalDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [acked, setAcked] = useState<Set<string>>(new Set());
  const [renumberOpen, setRenumberOpen] = useState(false);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    (async () => {
      try {
        const [server, local, a] = await Promise.all([
          listFieldPhotosForJob(jobId),
          getItemsByJob(jobId),
          getAppraisal(jobId).catch(() => null),
        ]);
        if (cancelled) return;
        setPhotos(mergeForReview(server, local));
        setJob(a);
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  const flagged = useMemo(
    () => photos?.filter((p) => p.device_info.flagged === true) ?? [],
    [photos],
  );

  const categoryBreakdown = useMemo(() => {
    if (!photos) return [] as { name: string; count: number }[];
    const map = new Map<string, number>();
    for (const p of photos) {
      const c = p.device_info.context?.category ?? 'Uncategorized';
      map.set(c, (map.get(c) ?? 0) + 1);
    }
    return Array.from(map.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [photos]);

  const missingElevations = useMemo(() => {
    if (!photos) return [] as string[];
    const seen = new Set<string>();
    for (const p of photos) {
      const e = p.device_info.context?.elevation;
      if (e && typeof e === 'string') seen.add(e.toLowerCase());
    }
    return ELEVATIONS.filter((e) => !seen.has(e));
  }, [photos]);

  const gapInfo = useMemo(() => {
    if (!photos || photos.length === 0)
      return { hasGaps: false, max: 0, gaps: [] as number[], count: 0 };
    const present = new Set(photos.map((p) => p.sequence_index));
    const max = Math.max(...photos.map((p) => p.sequence_index));
    const gaps: number[] = [];
    for (let i = 1; i <= max; i++) {
      if (!present.has(i)) gaps.push(i);
    }
    return { hasGaps: gaps.length > 0, max, gaps, count: photos.length };
  }, [photos]);

  const allAcked = flagged.every((p) => acked.has(p.id));
  const canComplete = allAcked && (photos?.length ?? 0) > 0;

  function toggleAck(id: string) {
    setAcked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleComplete() {
    if (!canComplete) return;
    if (gapInfo.hasGaps) {
      setRenumberOpen(true);
      return;
    }
    navigate(`/job/${jobId}/sync?renumber=0`);
  }

  function resolveRenumber(choice: 'renumber' | 'keep_gaps') {
    setRenumberOpen(false);
    navigate(
      `/job/${jobId}/sync?renumber=${choice === 'renumber' ? '1' : '0'}&choice=${choice}`,
    );
  }

  if (!jobId) return <div className="p-4 text-sm text-red-600">Missing job ID.</div>;
  if (err) {
    return (
      <div className="mx-auto max-w-2xl p-4">
        <Link to={`/job/${jobId}`} className="text-sm text-gray-600 underline">
          ‹ Job
        </Link>
        <div className="mt-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {err}
        </div>
      </div>
    );
  }
  if (!photos) {
    return <div className="p-4 text-sm text-gray-500">Loading…</div>;
  }

  const addressLine =
    job?.property_address ??
    [job?.address_street, job?.address_city, job?.address_state]
      .filter(Boolean)
      .join(', ') ??
    '';

  return (
    <div className="mx-auto max-w-2xl p-4">
      <Link to={`/job/${jobId}`} className="text-sm text-gray-600 underline">
        ‹ Job
      </Link>
      <h1 className="mt-2 text-xl font-bold">Pre-Sync Review</h1>
      <div className="text-xs text-gray-500">
        {job?.policyholder_name}
        {job?.claim_number && ` · Claim ${job.claim_number}`}
      </div>
      {addressLine && <div className="text-xs text-gray-500">{addressLine}</div>}

      {/* Summary */}
      <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3">
        <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500">
          Photos captured
        </div>
        <div className="mt-1 flex items-baseline gap-3">
          <div className="text-3xl font-bold">{photos.length}</div>
          {flagged.length > 0 && (
            <div className="text-sm text-red-500">{flagged.length} flagged</div>
          )}
          {gapInfo.hasGaps && (
            <div className="text-sm text-bracket-amber">
              gaps at {gapInfo.gaps.map((g) => `#${g}`).join(', ')}
            </div>
          )}
        </div>
        {categoryBreakdown.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {categoryBreakdown.map((c) => (
              <span
                key={c.name}
                className="flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs"
              >
                <span
                  className={`inline-block h-2 w-2 rounded-full ${categoryDotClass(c.name)}`}
                />
                <span>
                  {c.name} · {c.count}
                </span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Warnings */}
      {missingElevations.length > 0 && (
        <div className="mt-3 rounded-lg border border-bracket-amber/50 bg-bracket-amber/10 p-3">
          <div className="text-[10px] font-bold uppercase tracking-wide text-bracket-amber">
            Missing elevations
          </div>
          <div className="mt-1 text-sm">
            No photos tagged for:{' '}
            <span className="font-semibold capitalize">
              {missingElevations.join(', ')}
            </span>
            . You can still proceed, but consider heading back for what you missed.
          </div>
        </div>
      )}

      {/* Flagged ack list */}
      {flagged.length > 0 && (
        <div className="mt-3 rounded-lg border border-red-300 bg-red-50 p-3">
          <div className="text-[10px] font-bold uppercase tracking-wide text-red-600">
            Flagged photos — acknowledge each before sync
          </div>
          <ul className="mt-2 space-y-2">
            {flagged.map((p) => {
              const di = p.device_info;
              const isAcked = acked.has(p.id);
              return (
                <li
                  key={p.id}
                  onClick={() => toggleAck(p.id)}
                  className={`flex cursor-pointer items-start gap-3 rounded-md border p-2 ${
                    isAcked
                      ? 'border-eva-teal bg-eva-teal/10'
                      : 'border-red-300 bg-white'
                  }`}
                >
                  <span
                    className={`mt-0.5 inline-block h-2.5 w-2.5 shrink-0 rounded-full ${categoryDotClass(
                      di.context?.category,
                    )}`}
                  />
                  <div className="min-w-0 flex-1 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">#{p.sequence_index}</span>
                      <span className="truncate text-gray-600">
                        {di.context?.label}
                        {di.context?.description && ` — ${di.context.description}`}
                      </span>
                    </div>
                    {di.note_text && (
                      <div className="mt-0.5 text-xs text-gray-600">{di.note_text}</div>
                    )}
                  </div>
                  <span
                    className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border text-xs ${
                      isAcked
                        ? 'border-eva-teal bg-eva-teal text-white'
                        : 'border-gray-300'
                    }`}
                  >
                    {isAcked ? '✓' : ''}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* CTA */}
      <button
        onClick={handleComplete}
        disabled={!canComplete}
        className="mt-6 w-full rounded-lg bg-eva-teal px-4 py-3 text-base font-semibold text-white disabled:opacity-40"
      >
        Complete & Sync to EVA
      </button>

      {flagged.length > 0 && !allAcked && (
        <div className="mt-2 text-center text-xs text-gray-500">
          {flagged.length - acked.size} flagged photo
          {flagged.length - acked.size === 1 ? '' : 's'} still to acknowledge
        </div>
      )}

      {/* Renumber prompt */}
      {renumberOpen && (
        <RenumberPrompt
          gapCount={gapInfo.gaps.length}
          gaps={gapInfo.gaps}
          finalCount={gapInfo.count}
          onCancel={() => setRenumberOpen(false)}
          onChoose={resolveRenumber}
        />
      )}
    </div>
  );
}

function RenumberPrompt({
  gapCount,
  gaps,
  finalCount,
  onCancel,
  onChoose,
}: {
  gapCount: number;
  gaps: number[];
  finalCount: number;
  onCancel: () => void;
  onChoose: (choice: 'renumber' | 'keep_gaps') => void;
}) {
  const [choice, setChoice] = useState<'renumber' | 'keep_gaps' | null>(null);
  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/60 p-4 sm:items-center"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-t-2xl bg-white p-4 sm:rounded-2xl"
      >
        <h2 className="text-lg font-bold">Sequence gaps detected</h2>
        <p className="mt-1 text-sm text-gray-600">
          {gapCount} {gapCount === 1 ? 'gap' : 'gaps'} in the sequence at{' '}
          {gaps.map((g) => `#${g}`).join(', ')}. {finalCount} photos will ship.
        </p>
        <div className="mt-4 space-y-2">
          <button
            onClick={() => setChoice('renumber')}
            className={`w-full rounded-lg border p-3 text-left ${
              choice === 'renumber'
                ? 'border-eva-teal bg-eva-teal/10'
                : 'border-gray-300 bg-white'
            }`}
          >
            <div className="font-semibold">Renumber (close gaps)</div>
            <div className="text-xs text-gray-600">
              Photos will be resequenced 1 through {finalCount} before upload.
            </div>
          </button>
          <button
            onClick={() => setChoice('keep_gaps')}
            className={`w-full rounded-lg border p-3 text-left ${
              choice === 'keep_gaps'
                ? 'border-eva-teal bg-eva-teal/10'
                : 'border-gray-300 bg-white'
            }`}
          >
            <div className="font-semibold">Keep gaps</div>
            <div className="text-xs text-gray-600">
              Original numbering preserved for the audit trail.
            </div>
          </button>
        </div>
        <div className="mt-4 flex gap-2">
          <button
            onClick={onCancel}
            className="flex-1 rounded-lg border border-gray-300 bg-white py-2 text-sm font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={() => choice && onChoose(choice)}
            disabled={!choice}
            className="flex-1 rounded-lg bg-eva-teal py-2 text-sm font-semibold text-white disabled:opacity-40"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}
